// Reportes gerenciales del hotel (script 936): indicadores diarios (lo pasado
// es lo realizado; lo futuro, lo reservado), comparación con el año anterior,
// mezcla del negocio, listados del día imprimibles y cuadre contable.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, fieldCls, money, num, hotelRpc, QK, fmtFecha, fmtCorta, hoyISO, sumarDias, useSort, SortTh } from './shared';

type Tab = 'indicadores' | 'pronostico' | 'mezcla' | 'listados' | 'cuadre';
interface Dia {
  fecha: string; realizado: boolean; ocupadas: number; tentativas: number; ocupacion_pct: number; adr: number; revpar: number;
  ingreso_alojamiento: number; ingreso_ayb: number; ingreso_otros: number; llegadas: number; salidas: number; no_shows: number;
  ly_ocupadas: number; ly_ocupacion_pct: number; ly_adr: number;
}
interface Indicadores { fecha_hotel: string; habitaciones: number; dias: Dia[];
  totales: { noches_vendidas: number; noches_disponibles: number; ocupacion_pct: number; adr: number; revpar: number; ingreso_alojamiento: number;
    ingreso_ayb: number; ingreso_otros: number; ly_noches: number; ly_ingreso_alojamiento: number; llegadas: number; no_shows: number } }
interface Mezcla { clave: string; reservas: number; noches: number; ingreso: number; adr: number; participacion_pct: number }
interface FilaListado { id: string; codigo: string; huesped: string | null; telefono: string | null; habitacion: string | null; tipo: string; plan: string | null;
  adultos: number; ninos: number; llegada: string; salida: string; hora_llegada: string | null; garantizada: boolean; notas: string | null; saldo: number | null; deposito: number; grupo: string | null }
interface Cuadre {
  huespedes_en_casa: { contabilidad: number; folios_sin_facturar: number; facturado_sin_contabilizar: number; diferencia: number };
  anticipos: { contabilidad: number; folios: number; diferencia: number };
  folios_abiertos_antiguos: { codigo: string; estado: string; salida: string; saldo: number }[];
  depositos_sin_aplicar: { codigo: string; estado: string; disponible: number }[];
}
const DIMS: [string, string][] = [['canal', 'Canal'], ['plan', 'Plan'], ['tipo', 'Tipo de habitación'], ['nacionalidad', 'Nacionalidad'], ['agencia', 'Agencia / empresa']];
const CANAL: Record<string, string> = { recepcion: 'Recepción', telefono: 'Teléfono', directo: 'Web directa', empresa: 'Empresa / grupo', ota: 'OTA', ical: 'iCal' };

function Kpi({ label, valor, sub }: { label: string; valor: string; sub?: string }) {
  return (
    <Card><CardContent className="p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{valor}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </CardContent></Card>
  );
}
const varPct = (a: number, b: number) => (b > 0 ? `${a >= b ? '+' : ''}${num(100 * (a - b) / b, 1)} % vs año anterior` : 'sin datos del año anterior');

export function ReportesHotel() {
  const [tab, setTab] = useState<Tab>('indicadores');
  const btn = (k: Tab, l: string) => (
    <button key={k} type="button" onClick={() => setTab(k)}
      className={`rounded px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>{l}</button>
  );
  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><BarChart3 className="h-5 w-5" /> Reportes del hotel</h1>
        <div className="flex flex-wrap gap-1 rounded-md border p-1">
          {btn('indicadores', 'Indicadores')}{btn('pronostico', 'Pronóstico 90 días')}{btn('mezcla', 'Mezcla')}{btn('listados', 'Llegadas / salidas')}{btn('cuadre', 'Cuadre contable')}
        </div>
      </div>
      {tab === 'indicadores' && <TabIndicadores />}
      {tab === 'pronostico' && <TabPronostico />}
      {tab === 'mezcla' && <TabMezcla />}
      {tab === 'listados' && <TabListados />}
      {tab === 'cuadre' && <TabCuadre />}
    </div>
  );
}

function TablaDias({ dias, mostrarLy }: { dias: Dia[]; mostrarLy: boolean }) {
  return (
    <table className="w-full text-xs">
      <thead><tr className="border-b text-left text-muted-foreground">
        <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Fecha</th><th className="py-1.5 pr-2 text-right">Ocupadas</th>
        <th className="py-1.5 pr-2 text-right">Ocup. %</th><th className="py-1.5 pr-2 text-right">ADR</th><th className="py-1.5 pr-2 text-right">RevPAR</th>
        <th className="py-1.5 pr-2 text-right">Alojamiento</th><th className="py-1.5 pr-2 text-right">A&amp;B</th><th className="py-1.5 pr-2 text-right">Otros</th>
        <th className="py-1.5 pr-2 text-right">Lleg.</th><th className="py-1.5 pr-2 text-right">Sal.</th><th className="py-1.5 pr-2 text-right">No-show</th>
        {mostrarLy && <th className="py-1.5 pr-2 text-right">Ocup. % año ant.</th>}
      </tr></thead>
      <tbody>
        {dias.map((d, i) => (
          <tr key={d.fecha} className={`border-b last:border-0 ${d.realizado ? '' : 'text-sky-800 dark:text-sky-300'}`}>
            <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
            <td className="py-1 pr-2 whitespace-nowrap">{fmtCorta(d.fecha)} {fmtFecha(d.fecha).slice(3)}{!d.realizado && <span className="ml-1 text-[10px]">(reservado)</span>}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{d.ocupadas}{d.tentativas > 0 && <span className="ml-1 text-amber-600">+{d.tentativas}</span>}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{num(d.ocupacion_pct, 1)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{money(d.adr)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{money(d.revpar)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{money(d.ingreso_alojamiento)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{money(d.ingreso_ayb)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{money(d.ingreso_otros)}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{d.llegadas}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{d.salidas}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{d.no_shows}</td>
            {mostrarLy && <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{num(d.ly_ocupacion_pct, 1)}</td>}
          </tr>
        ))}
      </tbody>
      {dias.length > 0 && (
        <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
          <td className="py-1.5 pr-2" colSpan={2}>Totales</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{dias.reduce((a, d) => a + d.ocupadas, 0)}</td>
          <td colSpan={3} />
          <td className="py-1.5 pr-2 text-right tabular-nums">{money(dias.reduce((a, d) => a + Number(d.ingreso_alojamiento), 0))}</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{money(dias.reduce((a, d) => a + Number(d.ingreso_ayb), 0))}</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{money(dias.reduce((a, d) => a + Number(d.ingreso_otros), 0))}</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{dias.reduce((a, d) => a + d.llegadas, 0)}</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{dias.reduce((a, d) => a + d.salidas, 0)}</td>
          <td className="py-1.5 pr-2 text-right tabular-nums">{dias.reduce((a, d) => a + d.no_shows, 0)}</td>
          {mostrarLy && <td />}
        </tr></tfoot>
      )}
    </table>
  );
}

function TabIndicadores() {
  const hoy = hoyISO();
  const [desde, setDesde] = useState(`${hoy.slice(0, 7)}-01`);
  const [hasta, setHasta] = useState(hoy);
  const { data, isLoading } = useQuery<Indicadores>({ queryKey: [QK, 'rep-ind', desde, hasta], queryFn: () => hotelRpc<Indicadores>('reporte_indicadores', { _desde: desde, _hasta: hasta }) });
  const t = data?.totales;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" /> Imprimir</Button>
      </div>
      {t && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          <Kpi label="Ocupación" valor={`${num(t.ocupacion_pct, 1)} %`} sub={`${t.noches_vendidas} de ${t.noches_disponibles} noches`} />
          <Kpi label="ADR (tarifa media)" valor={money(t.adr)} />
          <Kpi label="RevPAR" valor={money(t.revpar)} />
          <Kpi label="Ingreso alojamiento" valor={money(t.ingreso_alojamiento)} sub={varPct(Number(t.ingreso_alojamiento), Number(t.ly_ingreso_alojamiento))} />
          <Kpi label="A&B y otros" valor={money(Number(t.ingreso_ayb) + Number(t.ingreso_otros))} sub={`A&B ${money(t.ingreso_ayb)} · otros ${money(t.ingreso_otros)}`} />
          <Kpi label="Llegadas / no-shows" valor={`${t.llegadas} / ${t.no_shows}`} />
        </div>
      )}
      <Card><CardContent className="overflow-x-auto p-3">
        {isLoading ? <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p> : <TablaDias dias={data?.dias ?? []} mostrarLy />}
        <p className="mt-2 text-xs text-muted-foreground">En azul, noches aún no cerradas (lo reservado). «+n» = reservas tentativas, que no cuentan como ocupación. Habitaciones vendibles: {data?.habitaciones} (sin las fuera de orden).</p>
      </CardContent></Card>
    </div>
  );
}

function TabPronostico() {
  const hoy = hoyISO();
  const { data, isLoading } = useQuery<Indicadores>({ queryKey: [QK, 'rep-fc', hoy], queryFn: () => hotelRpc<Indicadores>('reporte_indicadores', { _desde: hoy, _hasta: sumarDias(hoy, 89) }) });
  const dias = data?.dias ?? [];
  const max = Math.max(1, data?.habitaciones ?? 1);
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Ocupación reservada — próximos 90 días</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <p className="text-sm text-muted-foreground">Cargando…</p> : (
            <div className="flex h-40 items-end gap-px">
              {dias.map((d) => (
                <div key={d.fecha} className="group relative flex-1" title={`${fmtFecha(d.fecha)}: ${d.ocupadas} ocupadas (${num(d.ocupacion_pct, 1)} %)${d.tentativas ? ` + ${d.tentativas} tentativas` : ''} · año anterior ${num(d.ly_ocupacion_pct, 1)} %`}>
                  <div className="w-full bg-amber-300/70" style={{ height: `${(100 * d.tentativas) / max}%` }} />
                  <div className={`w-full ${d.ocupacion_pct >= 80 ? 'bg-emerald-500' : d.ocupacion_pct >= 50 ? 'bg-sky-500' : 'bg-slate-400'}`} style={{ height: `${(100 * d.ocupadas) / max}%` }} />
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">Barras: confirmadas y en casa (verde ≥ 80 %, azul ≥ 50 %); en ámbar, tentativas. Pase el mouse para ver el día y el año anterior.</p>
        </CardContent>
      </Card>
      <Card><CardContent className="overflow-x-auto p-3"><TablaDias dias={dias} mostrarLy /></CardContent></Card>
    </div>
  );
}

function TabMezcla() {
  const hoy = hoyISO();
  const [desde, setDesde] = useState(`${hoy.slice(0, 7)}-01`);
  const [hasta, setHasta] = useState(sumarDias(hoy, 30));
  const [dim, setDim] = useState('canal');
  const { data = [], isLoading } = useQuery<Mezcla[]>({ queryKey: [QK, 'rep-mix', desde, hasta, dim], queryFn: () => hotelRpc<Mezcla[]>('reporte_mezcla', { _desde: desde, _hasta: hasta, _dim: dim }) });
  const sort = useSort(data, (r, k) => (k === 'clave' ? r.clave : Number((r as unknown as Record<string, number>)[k])), 'ingreso', 'desc');
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Por"><select className={fieldCls} value={dim} onChange={(e) => setDim(e.target.value)}>{DIMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Campo>
      </div>
      <Card><CardContent className="overflow-x-auto p-3">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">N°</th>
            <SortTh col="clave" sort={sort}>{DIMS.find(([k]) => k === dim)?.[1]}</SortTh>
            <SortTh col="reservas" sort={sort} align="right">Reservas</SortTh>
            <SortTh col="noches" sort={sort} align="right">Noches</SortTh>
            <SortTh col="ingreso" sort={sort} align="right">Ingreso alojamiento</SortTh>
            <SortTh col="adr" sort={sort} align="right">ADR</SortTh>
            <SortTh col="participacion_pct" sort={sort} align="right">Participación</SortTh>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>}
            {!isLoading && data.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin noches vendidas en el rango.</td></tr>}
            {sort.sorted.map((m, i) => (
              <tr key={m.clave} className="border-b last:border-0">
                <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-3">{dim === 'canal' ? CANAL[m.clave] ?? m.clave : m.clave}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{m.reservas}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{m.noches}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(m.ingreso)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{money(m.adr)}</td>
                <td className="py-1.5 pr-3 text-right">
                  <div className="flex items-center justify-end gap-2"><div className="h-2 w-24 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${m.participacion_pct}%` }} /></div>
                  <span className="w-12 tabular-nums">{num(m.participacion_pct, 1)} %</span></div>
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={2} className="py-2">Totales</td>
            <td className="py-2 pr-3 text-right tabular-nums">{data.reduce((a, m) => a + m.reservas, 0)}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{data.reduce((a, m) => a + m.noches, 0)}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{money(data.reduce((a, m) => a + Number(m.ingreso), 0))}</td>
            <td colSpan={2} />
          </tr></tfoot>}
        </table>
      </CardContent></Card>
    </div>
  );
}

function TabListados() {
  const [tipo, setTipo] = useState<'llegadas' | 'salidas' | 'en_casa'>('llegadas');
  const [fecha, setFecha] = useState('');
  const { data, isLoading } = useQuery<{ fecha: string; filas: FilaListado[] }>({
    queryKey: [QK, 'rep-lst', tipo, fecha], queryFn: () => hotelRpc('listado_dia', { _fecha: fecha || null, _tipo: tipo }),
  });
  const filas = useMemo(() => data?.filas ?? [], [data]);
  const titulo = { llegadas: 'Llegadas', salidas: 'Salidas', en_casa: 'Huéspedes en casa' }[tipo];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <Campo label="Listado"><select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="llegadas">Llegadas</option><option value="salidas">Salidas</option><option value="en_casa">En casa</option></select></Campo>
        <Campo label="Fecha de hotel"><input type="date" className={fieldCls} value={fecha || data?.fecha || ''} onChange={(e) => setFecha(e.target.value)} /></Campo>
        <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" /> Imprimir</Button>
      </div>
      <h2 className="text-base font-semibold">{titulo} · {fmtFecha(data?.fecha)} ({filas.length})</h2>
      <Card><CardContent className="overflow-x-auto p-3">
        <table className="w-full text-xs">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Hab.</th><th className="py-1.5 pr-2">Reserva</th><th className="py-1.5 pr-2">Huésped</th>
            <th className="py-1.5 pr-2">Tipo / plan</th><th className="py-1.5 pr-2 text-right">Pax</th><th className="py-1.5 pr-2">Llegada</th><th className="py-1.5 pr-2">Salida</th>
            <th className="py-1.5 pr-2">Hora</th><th className="py-1.5 pr-2 text-right">Saldo</th><th className="py-1.5 pr-2">Notas</th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={11} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>}
            {!isLoading && filas.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-muted-foreground">Sin movimientos.</td></tr>}
            {filas.map((f, i) => (
              <tr key={f.id} className="border-b last:border-0 align-top">
                <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1 pr-2 font-mono font-semibold">{f.habitacion ?? '—'}</td>
                <td className="py-1 pr-2 font-mono">{f.codigo}{f.grupo && <div className="text-[10px] text-muted-foreground">{f.grupo}</div>}</td>
                <td className="py-1 pr-2">{f.huesped ?? '—'}{f.telefono && <div className="text-[10px] text-muted-foreground">{f.telefono}</div>}</td>
                <td className="py-1 pr-2">{f.tipo}{f.plan && <div className="text-[10px] text-muted-foreground">{f.plan}</div>}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{f.adultos}{f.ninos ? `+${f.ninos}` : ''}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{fmtFecha(f.llegada)}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{fmtFecha(f.salida)}</td>
                <td className="py-1 pr-2">{f.hora_llegada?.slice(0, 5) ?? '—'}{f.garantizada && <span className="ml-1 text-amber-700">G</span>}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{f.saldo != null ? money(Number(f.saldo)) : '—'}</td>
                <td className="py-1 pr-2 text-muted-foreground">{f.notas ?? ''}</td>
              </tr>
            ))}
          </tbody>
          {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={5} className="py-1.5">Total: {filas.length} habitaciones</td>
            <td className="py-1.5 pr-2 text-right tabular-nums">{filas.reduce((a, f) => a + f.adultos + f.ninos, 0)}</td>
            <td colSpan={3} />
            <td className="py-1.5 pr-2 text-right tabular-nums">{money(filas.reduce((a, f) => a + Number(f.saldo ?? 0), 0))}</td>
            <td />
          </tr></tfoot>}
        </table>
      </CardContent></Card>
    </div>
  );
}

function TabCuadre() {
  const { data: c, isLoading } = useQuery<Cuadre>({ queryKey: [QK, 'rep-cuadre'], queryFn: () => hotelRpc<Cuadre>('cuadre_contable', {}) });
  if (isLoading || !c) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const ok = (d: number) => Math.abs(Number(d)) < 0.01;
  const Fila = ({ l, v, fuerte }: { l: string; v: number; fuerte?: boolean }) => (
    <div className={`flex justify-between py-0.5 ${fuerte ? 'border-t pt-1 font-semibold' : ''}`}><span>{l}</span><span className="font-mono">{money(Number(v))}</span></div>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Huéspedes en casa</CardTitle></CardHeader>
        <CardContent className="text-sm">
          <Fila l="Saldo en contabilidad" v={c.huespedes_en_casa.contabilidad} />
          <Fila l="Cargos de folio sin facturar" v={c.huespedes_en_casa.folios_sin_facturar} />
          <Fila l="Facturados sin contabilizar (sin autorizar)" v={c.huespedes_en_casa.facturado_sin_contabilizar} />
          <Fila l="Diferencia" v={c.huespedes_en_casa.diferencia} fuerte />
          <p className={`mt-2 rounded px-2 py-1 text-xs ${ok(c.huespedes_en_casa.diferencia) ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
            {ok(c.huespedes_en_casa.diferencia) ? 'Cuadra con los folios.' : 'No cuadra: revise asientos manuales a esta cuenta o la configuración contable del hotel.'}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Anticipos de huéspedes</CardTitle></CardHeader>
        <CardContent className="text-sm">
          <Fila l="Saldo en contabilidad" v={c.anticipos.contabilidad} />
          <Fila l="Depósitos sin aplicar en folios" v={c.anticipos.folios} />
          <Fila l="Diferencia" v={c.anticipos.diferencia} fuerte />
          <p className={`mt-2 rounded px-2 py-1 text-xs ${ok(c.anticipos.diferencia) ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
            {ok(c.anticipos.diferencia) ? 'Cuadra con los folios.' : 'No cuadra: hay depósitos movidos fuera del hotel o asientos manuales.'}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Depósitos sin aplicar en reservas cerradas ({c.depositos_sin_aplicar.length})</CardTitle></CardHeader>
        <CardContent className="text-xs">
          {c.depositos_sin_aplicar.length === 0 ? <p className="text-muted-foreground">Ninguno.</p> : c.depositos_sin_aplicar.map((d) => (
            <div key={d.codigo} className="flex justify-between border-b py-1 last:border-0"><span className="font-mono">{d.codigo} · {d.estado}</span><span className="font-mono">{money(Number(d.disponible))}</span></div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Folios abiertos de reservas ya cerradas ({c.folios_abiertos_antiguos.length})</CardTitle></CardHeader>
        <CardContent className="text-xs">
          {c.folios_abiertos_antiguos.length === 0 ? <p className="text-muted-foreground">Ninguno.</p> : c.folios_abiertos_antiguos.map((d) => (
            <div key={d.codigo} className="flex justify-between border-b py-1 last:border-0"><span className="font-mono">{d.codigo} · {d.estado} · salida {fmtFecha(d.salida)}</span><span className="font-mono">{money(Number(d.saldo))}</span></div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
